'use client'
import { BannerDecision } from '@/app/api/placements/types'
import NavigateRightIcon from '@/app/icons/navigate-right'
import { DeviceType } from '@/app/util/env'
import Attribution from '@/components/attribution-v2'
import { AttrOverlay } from '@/components/grid/overlay/attr-overlay'
import UserContext from '@/context/user-context'
import { getAdBannerData } from '@/util/ad-banner'
import { fireAdTrackingPixel, handleAdCtaClick } from '@/util/ad-click'
import { cn } from '@/util/cn'
import { pingback, PingbackActionType } from '@giphy/js-analytics'
import { IGif } from '@giphy/js-types'
import { SyntheticEvent, useContext } from 'react'
import { InView } from 'react-intersection-observer'

const BannerOverlay = ({
    bannerData,
    deviceType,
    gif,
    onCtaClick,
    width,
}: {
    bannerData: BannerDecision
    deviceType: DeviceType
    gif: IGif
    onCtaClick: (e: SyntheticEvent) => void
    width: number
}) => {
    const { click_url, cta_text, cta_url } = bannerData
    const isMobile = deviceType === 'mobile'
    const shouldRenderCta = Boolean(cta_text && (cta_url || click_url))

    // no attribution or CTA on non-XL banners
    if (isMobile && (!gif.user || gif.images.original.height / gif.images.original.width < 0.5)) return null

    return (
        <div className="pointer-events-none absolute inset-0 isolate">
            {isMobile ? (
                <div
                    className="pointer-events-none absolute inset-0 overflow-hidden"
                    data-testid="centurion-banner-attribution-compact"
                >
                    <AttrOverlay
                        asLink
                        gif={gif}
                        isHovered
                        width={width}
                        attributionClassName={shouldRenderCta ? 'max-w-[48%]' : 'max-w-[calc(100%-1.25rem)]'}
                    />
                    {shouldRenderCta && (
                        <button
                            type="button"
                            data-testid="centurion-banner-cta"
                            aria-label={cta_text}
                            className="pointer-events-auto absolute right-2 bottom-2 flex h-8 max-w-[43%] min-w-0 cursor-pointer items-center justify-center gap-2 font-bold focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-giphyBlue"
                            onClick={onCtaClick}
                        >
                            <span className="truncate">{cta_text}</span>
                            <NavigateRightIcon className="size-3 shrink-0" />
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {gif.user && (
                        <div
                            className="pointer-events-auto absolute bottom-2 left-2 max-w-[60%]"
                            data-testid="centurion-banner-attribution-full"
                        >
                            <Attribution user={gif.user} href={`/${gif.user.username}`} className="text-shadow-lg/10" />
                        </div>
                    )}
                    {shouldRenderCta && (
                        <button
                            type="button"
                            data-testid="centurion-banner-cta"
                            className="pointer-events-auto absolute right-2 bottom-2 flex h-12.5 cursor-pointer items-center justify-center gap-2 rounded-md bg-giphyBlack/20 px-5 font-bold text-giphyWhite/75 hover:underline"
                            onClick={onCtaClick}
                        >
                            {cta_text} <NavigateRightIcon className="size-3 fill-giphyWhite/75" />
                        </button>
                    )}
                </>
            )}
        </div>
    )
}

const CenturionBanner = ({
    className,
    aspectRatio,
    bannerData,
    deviceType,
}: {
    className?: string
    aspectRatio?: number
    bannerData: BannerDecision
    deviceType: DeviceType
}) => {
    const { user } = useContext(UserContext)
    const gif = bannerData?.gif
    if (!gif) {
        return null
    }

    const { url, fileType } = getAdBannerData(gif, deviceType)
    if (!url) {
        return null
    }

    const {
        click_url,
        click_tracking_url,
        cta_text,
        cta_tracking_url,
        cta_url,
        impression_tracking_url: impression_url,
        third_party_tag,
    } = bannerData
    const { width, height } = gif.images.original

    const trackAction = (actionType: PingbackActionType) => {
        pingback({
            userId: user ? user.id : undefined,
            actionType,
            analyticsResponsePayload: gif.analytics_response_payload,
        })
    }

    const onClickHandler = () => {
        trackAction('CLICK')
        fireAdTrackingPixel(click_tracking_url)
    }

    const onCtaClick = (e: SyntheticEvent) => {
        e.preventDefault()
        e.stopPropagation()
        trackAction('CLICK')
        handleAdCtaClick({
            ctaTrackingUrl: cta_tracking_url || click_tracking_url,
            ctaUrl: cta_url || click_url,
        })
    }

    const onMouseEnterHandler = () => {
        trackAction('HOVER')
    }

    const onView = (inView: boolean): void => {
        if (inView) {
            try {
                fireAdTrackingPixel(impression_url)
                fireAdTrackingPixel(third_party_tag)
            } catch (e) {
                console.error(`Error with tracking pixel for geotargeted banner`, e)
            }
        }
    }

    return (
        <InView
            key={url}
            as="div"
            onChange={onView}
            onMouseEnter={onMouseEnterHandler}
            triggerOnce
            threshold={1}
            className={cn('ci-removed relative block overflow-hidden transition-all duration-100', className)}
            style={{ aspectRatio: aspectRatio || width / height }}
        >
            <a
                href={click_url}
                onClick={onClickHandler}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="centurion-banner-link"
                className={cn('block h-full w-full', click_url && 'cursor-pointer')}
            >
                {fileType === 'video' ? (
                    <video
                        src={url}
                        title="Home banner"
                        autoPlay
                        loop
                        muted
                        className="ci-hidden h-full w-full object-contain"
                        data-testid="centurion-banner-media"
                        style={{ aspectRatio }}
                    />
                ) : (
                    <img
                        className="h-full w-full object-cover"
                        alt={cta_text}
                        src={url}
                        title="Home banner"
                        data-testid="centurion-banner-media"
                    />
                )}
            </a>
            <BannerOverlay
                bannerData={bannerData}
                deviceType={deviceType}
                gif={gif}
                onCtaClick={onCtaClick}
                width={Number(width)}
            />
        </InView>
    )
}

export default CenturionBanner
